import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import AssetsGraphIcon from '@atlaskit/icon-lab/core/assets-graph';

import { ANALYTICS_SOURCE, OPEN_GRAPH_ACTION_SUBJECT_ID } from '../analytics.ts';

const i18n = defineMessages({
	label: {
		id: 'byline-connections.preview-panel.open-graph-button.label',
		defaultMessage: 'Open graph view',
		description:
			'Accessible name and tooltip of the graph icon button in the Teamwork Graph preview panel header. Pressing it opens the full-size Teamwork Graph modal.',
	},
});

export type TeamworkGraphPreviewPanelOpenGraphButtonProps = {
	onClick: () => void;
};

/**
 * The preview panel header's entry to the full-size graph. The in-panel canvas preview used to
 * be that affordance; the header button is now the only opener.
 */
export const TeamworkGraphPreviewPanelOpenGraphButton = ({
	onClick,
}: TeamworkGraphPreviewPanelOpenGraphButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: OPEN_GRAPH_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
			},
		}).fire();
		onClick();
	}, [createAnalyticsEvent, onClick]);

	return (
		<IconButton
			icon={AssetsGraphIcon}
			label={formatMessage(i18n.label)}
			appearance="subtle"
			isTooltipDisabled={false}
			onClick={handleClick}
			testId="teamwork-graph-preview-panel.open-graph-button"
		/>
	);
};
